import { useEffect, useRef, useState } from "react";

export default function Pagination({
  currentPage = 1,
  recordsPerPage = 10,
  setRecordsPerPage,
  onPrevious,
  onNext,
  totalRecord,
  title = "record",
  handleGoto,
  compact = false,
  pageSizeOptions = [5, 10, 15, 20, 25, 50, 100],
  className = "bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mt-4 py-2 px-2 mr-12",
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentPage);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) {
      setValue(currentPage);
    }
  }, [currentPage, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
      setValue(currentPage);
    }
  }, [editing, currentPage]);

  const startIndex = (currentPage - 1) * recordsPerPage + 1;
  const endIndex = Math.min(currentPage * recordsPerPage, totalRecord || 0);
  const totalPage = Math.ceil(totalRecord / recordsPerPage) || 1;

  const handleChange = (e) => {
    let val = e.target.value.replace(/[^0-9]/g, "");

    if (val && Number(val) < 1) {
      val = "1";
    } else if (val && Number(val) > totalPage) {
      val = String(totalPage);
    }

    setValue(val === "" ? "" : Number(val));
  };
  const handleblur = () => {
    setEditing(false);
    const nextPage = Number(value) || 1;
    setValue(nextPage);
    if (currentPage !== nextPage) {
      handleGoto?.(nextPage);
    }
  };
  const handlePageSize = (size) => {
    setRecordsPerPage(size);
  };
  const textCls = compact
    ? "text-[10px] text-slate-500 dark:text-slate-400"
    : "text-sm text-slate-500 dark:text-slate-400";
  const selectCls = compact
    ? "px-1.5 py-0.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded text-[10px] text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-teal-500"
    : "px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500";
  const btnCls = compact
    ? "px-1.5 py-0.5 text-[10px] border border-slate-300 dark:border-slate-600 rounded text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
    : "px-3 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50";
  // Idle = filled current page; editing = outlined input ready to type
  const pageBtnCls = compact
    ? "px-1.5 py-0.5 min-w-[1.75rem] h-[22px] text-[10px] font-semibold text-center rounded-md border-2 border-[#0072FF] bg-[#0072FF] text-white shadow-sm hover:bg-[#0060d6] hover:border-[#0060d6] disabled:opacity-50"
    : "px-3 py-1 min-w-[2.5rem] h-8 text-sm font-semibold text-center rounded-md border-2 border-[#0072FF] bg-[#0072FF] text-white shadow-sm hover:bg-[#0060d6] hover:border-[#0060d6] disabled:opacity-50";
  const pageInputCls = compact
    ? "px-1 py-0.5 w-10 h-[22px] text-[10px] font-semibold text-center rounded-md border-2 border-[#0072FF] bg-white dark:bg-slate-900 text-[#0072FF] outline-none shadow-sm ring-2 ring-[#0072FF]/35"
    : "px-3 py-1 w-14 h-8 text-sm font-semibold text-center rounded-md border-2 border-[#0072FF] bg-white dark:bg-slate-900 text-[#0072FF] outline-none shadow-sm ring-2 ring-[#0072FF]/35";

  return (
    <div className={className}>
      <div className={compact ? "flex flex-wrap items-center justify-between gap-x-2 gap-y-1" : "flex flex-col sm:flex-row items-center justify-between gap-4"}>
        <div className={`flex items-center gap-1.5 ${textCls}`}>
          <span>Show</span>
          <select
            value={recordsPerPage}
            disabled={!totalRecord}
            onChange={(e) => handlePageSize(Number(e.target.value))}
            className={selectCls}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          <span>entries</span>
        </div>

        <div className={`flex items-center gap-0.5 ${compact ? "" : "mr-4"}`}>
          <button
            onClick={() => onPrevious(currentPage - 1)}
            disabled={(currentPage === 1) || !totalRecord}
            className={btnCls}
          >
            {compact ? "Prev" : "Previous"}
          </button>

          <div className={`flex items-center gap-0.5 ${compact ? "mx-1" : "mx-2"}`}>
            <>
              {!editing ? (
                <button
                  type="button"
                  disabled={!handleGoto || !totalRecord}
                  onClick={() => setEditing(true)}
                  title="Go to page"
                  aria-label="Go to page"
                  className={pageBtnCls}
                >
                  {currentPage}
                </button>
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={value}
                  onChange={handleChange}
                  onBlur={handleblur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleblur();
                    }
                  }}
                  className={pageInputCls}
                  aria-label="Page number"
                />
              )}
            </>
          </div>

          <button
            onClick={() => onNext(currentPage + 1)}
            disabled={(currentPage === totalPage) || !totalRecord}
            className={btnCls}
          >
            Next
          </button>
        </div>

        <div className={`${textCls} ${compact ? "whitespace-nowrap" : "mt-0.5"}`}>
          {compact ? (
            <>
              {totalRecord ? startIndex || 0 : 0}–{endIndex || 0} / {totalRecord || 0}
            </>
          ) : (
            <>
              Showing {totalRecord ? (startIndex || 0) : 0} – {endIndex || 0} of {totalRecord || 0}{" "}
              {title}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
